import React, { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TierBadge, StatusBadge } from "@/components/Badges";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Coins, TrendingUp, Gift, ArrowUpRight, ArrowDownRight, Sparkles, Crown, Ticket, Lock, Loader2, HeartPulse, Award } from "lucide-react";
import { BadgeIcon } from "@/components/BadgeIcon";

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState<any>(null);
  const [badges, setBadges] = useState<any[]>([]);
  const ctx = useOutletContext<any>();
  const raffleFeat = ctx?.features?.raffle;
  const catalogAvailable = !ctx?.features || ctx.features.catalog?.available !== false;

  useEffect(() => {
    api.get("/customer/dashboard").then((r) => setData(r.data)).catch(() => {});
    api.get("/badges/me").then((r) => setBadges(r.data)).catch(() => {});
  }, []);

  if (!data)
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-7 h-7 text-gold animate-spin" />
      </div>
    );

  const u = data.user;

  return (
    <div className="space-y-8 animate-fade-in" data-testid="customer-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm text-slate-500">Hola de nuevo,</p>
          <h1 className="text-3xl font-serif font-medium">{u.name}</h1>
        </div>
        <StatusBadge status={u.status} />
      </div>

      {u.status === "overdue" && (
        <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 text-sm text-amber-700">
          Tu cuenta figura como <strong>no al día</strong>. Regulariza tu situación para desbloquear canjes premium.
        </div>
      )}

      {/* Upsell: planes Armony Contigo Plus y Premium */}
      {u.subscription_status !== "active" ? (
        <Link to="/portal/planes" data-testid="dashboard-plans-banner" className="block group">
          <div className="relative card-obsidian p-6 overflow-hidden border border-gold/20 hover:border-gold/40 transition-colors">
            <div className="relative flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl gold-gradient-bg flex items-center justify-center shrink-0">
                  <HeartPulse className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-gold-ink font-mono mb-1">Mejora tu bienestar</p>
                  <h3 className="font-serif text-lg font-medium">Suma salud mental y telepsicología con Armony Contigo Plus</h3>
                  <p className="text-sm text-slate-500">Desde $0.90/mes · contenido, autogestión y atención profesional.</p>
                </div>
              </div>
              <Button data-testid="dashboard-banner-cta" className="gold-gradient-bg text-white font-semibold rounded-full px-6 group-hover:scale-[1.03] transition-transform">
                Ver planes
              </Button>
            </div>
          </div>
        </Link>
      ) : (
        <div className={`p-4 rounded-xl border text-sm flex flex-wrap items-center gap-x-2 gap-y-2 ${u.subscription_cancel_at ? "bg-amber-500/10 border-amber-500/40 text-amber-900" : "bg-emerald-500/10 border-emerald-500/30 text-emerald-800"}`} data-testid="dashboard-plan-active">
          <HeartPulse className="w-4 h-4 shrink-0" aria-hidden="true" />
          <span className="flex-1 min-w-[12rem]">
            {u.subscription_cancel_at ? (
              <>Tu plan <strong>{u.plan === "fitrewards_plus" ? "Armony Contigo Premium" : "Armony Contigo Plus"}</strong> termina el{" "}
                <strong>{new Date(u.subscription_cancel_at).toLocaleDateString("es-EC", { day: "numeric", month: "long", year: "numeric", timeZone: "America/Guayaquil" })}</strong> y no se renovará.</>
            ) : (
              <>Tienes activo <strong>{u.plan === "fitrewards_plus" ? "Armony Contigo Premium" : "Armony Contigo Plus"}</strong>. ¡Disfruta tus beneficios de bienestar!</>
            )}
          </span>
          <Link to="/portal/planes" data-testid="dashboard-manage-plan" className="font-semibold underline underline-offset-2">
            {u.subscription_cancel_at ? "Mantener mi plan" : "Gestionar mi plan"}
          </Link>
        </div>
      )}

      {/* Hero points card */}
      <div className="relative card-obsidian p-8 overflow-hidden">
        <div className="relative grid md:grid-cols-2 gap-8 items-center">
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500 font-mono mb-2">Puntos disponibles</p>
            <div className="flex items-end gap-2">
              <span data-testid="customer-points-display" className="text-6xl font-serif font-semibold text-navy">
                {u.points.toLocaleString()}
              </span>
              <span className="text-slate-500 mb-2">pts</span>
            </div>
            <div className="mt-4 flex items-center gap-3">
              <TierBadge tier={u.tier} />
            </div>
          </div>
          <div>
            {u.tier_locked ? (
              <div className="flex items-center gap-3 text-slate-700" data-testid="tier-locked">
                <Crown className="w-8 h-8 text-gold" />
                <p className="text-sm">Armony te asignó el nivel <strong>{u.tier}</strong>. Lo mantienes hasta el 31 de diciembre, aunque canjees tus puntos.</p>
              </div>
            ) : u.next_tier ? (
              <>
                <div className="flex items-center justify-between mb-2 text-sm">
                  <span className="text-slate-500">Progreso a {u.next_tier}</span>
                  <span className="text-gold-ink font-mono">{u.progress}%</span>
                </div>
                <Progress value={u.progress} className="h-2.5 bg-slate-100" />
                <p className="text-sm text-slate-500 mt-3" data-testid="progress-to-next">
                  Te faltan <span className="text-gold-ink font-semibold">{u.points_to_next.toLocaleString()}</span> puntos ganados este año para
                  alcanzar el nivel <span className="text-slate-800">{u.next_tier}</span>. Canjear no te hace perder tu nivel.
                </p>
              </>
            ) : (
              <div className="flex items-center gap-3 text-slate-700">
                <Crown className="w-8 h-8 text-gold" />
                <p className="text-sm">¡Felicidades! Alcanzaste el máximo nivel <strong>VIP</strong>.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick stats */}
      <div className="grid sm:grid-cols-3 gap-5">
        <StatCard icon={Crown} label="Nivel actual" value={u.tier} sub={`Lo mantienes hasta el ${new Date(u.tier_valid_until + "T12:00:00").toLocaleDateString("es-EC", { day: "numeric", month: "long" })}, aunque canjees`} />
        <StatCard icon={TrendingUp} label="Siguiente nivel" value={u.tier_locked ? "—" : u.next_tier || "Máximo"} sub={u.tier_locked ? "Nivel asignado por Armony" : u.next_tier ? `${u.points_to_next.toLocaleString()} pts ganados este año restantes` : "Nivel tope"} />
        <StatCard icon={Award} label="Insignias" value={String(badges.length)} sub={badges.length ? "Reconocimientos de Armony" : "Aún no tienes insignias"} />
      </div>

      {badges.length > 0 && (
        <section className="card-obsidian p-6" aria-labelledby="my-badges-title" data-testid="my-badges">
          <h2 id="my-badges-title" className="text-lg font-serif font-medium mb-4">Mis insignias</h2>
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {badges.map((b) => (
              <li key={b.id} className="flex items-start gap-3">
                <BadgeIcon icon={b.icon} />
                <div>
                  <p className="font-medium text-slate-900">{b.name}</p>
                  <p className="text-sm text-slate-600">{b.reason || b.description}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{new Date(b.awarded_at).toLocaleDateString("es-EC", { day: "numeric", month: "long", year: "numeric", timeZone: "America/Guayaquil" })}</p>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Raffle tickets — sorteo 21 de diciembre */}
      {raffleFeat?.available && data.raffle && (
        <div className="relative card-obsidian p-6 overflow-hidden gold-glow" data-testid="raffle-card">
            <div className="relative flex flex-wrap items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl gold-gradient-bg flex items-center justify-center shrink-0">
                <Ticket className="w-7 h-7 text-white" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-slate-500 font-mono mb-1">Sorteo {data.raffle.draw_date}</p>
                <p className="text-sm text-slate-700">
                  Tienes <span data-testid="raffle-tickets" className="text-gold-ink font-serif text-2xl font-semibold align-middle">{data.raffle.tickets}</span>{" "}
                  boleto{data.raffle.tickets === 1 ? "" : "s"} acumulado{data.raffle.tickets === 1 ? "" : "s"}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs text-slate-500">Te faltan</p>
              <p className="text-sm text-slate-700"><span className="text-gold-ink font-semibold">{data.raffle.next_ticket_in}</span> pts para tu próximo boleto</p>
              <p className="text-xs text-slate-500 mt-1">1 boleto por cada {data.raffle.points_per_ticket} pts acumulados</p>
            </div>
          </div>
        </div>
      )}

      {/* Benefits + activity */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card-obsidian p-6">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-gold" />
            <h2 className="text-lg font-semibold">Beneficios de tu nivel {u.tier}</h2>
          </div>
          <ul className="space-y-3">
            {data.tier_benefits.map((b: string, i: number) => (
              <li key={i} className="flex items-start gap-3 text-sm text-slate-700">
                <span className="w-1.5 h-1.5 rounded-full bg-gold mt-1.5 shrink-0" />
                {b}
              </li>
            ))}
          </ul>
        </div>

        <div className="card-obsidian p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Actividad reciente</h2>
            <Link to="/portal/canjes" className="text-xs text-gold-ink hover:underline">Ver todo</Link>
          </div>
          {data.recent_transactions.length === 0 ? (
            <p className="text-sm text-slate-500">Aún no tienes movimientos.</p>
          ) : (
            <ul className="space-y-3">
              {data.recent_transactions.map((t: any) => (
                <li key={t.id} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${t.points >= 0 ? "bg-emerald-500/15" : "bg-red-500/15"}`}>
                      {t.points >= 0 ? <ArrowUpRight className="w-4 h-4 text-emerald-700" /> : <ArrowDownRight className="w-4 h-4 text-red-700" />}
                    </div>
                    <span className="text-slate-700 truncate">{t.reason}</span>
                  </div>
                  <span className={`font-mono font-semibold shrink-0 ml-2 ${t.points >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                    {t.points >= 0 ? "+" : ""}{t.points.toLocaleString()}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {catalogAvailable && (
      <div className="card-obsidian p-6 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Gift className="w-6 h-6 text-gold" />
          <div>
            <h3 className="font-semibold">Explora el catálogo de recompensas</h3>
            <p className="text-sm text-slate-500">Canjea tus puntos por experiencias de lujo.</p>
          </div>
        </div>
        <Link to="/portal/catalogo">
          <Button data-testid="dashboard-to-catalog" className="gold-gradient-bg text-white font-semibold rounded-full px-6">
            Ver catálogo
          </Button>
        </Link>
      </div>
      )}
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, sub }: any) => (
  <div className="card-obsidian p-5">
    <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wider font-mono mb-3">
      <Icon className="w-4 h-4 text-gold" /> {label}
    </div>
    <p className="text-2xl font-serif font-medium text-slate-900">{value}</p>
    <p className="text-xs text-slate-500 mt-1">{sub}</p>
  </div>
);
